import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ruler, Check, X, Loader2 } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { RoomVisual } from "../components/RoomVisual";
import { BadgePill } from "../components/ProductCard";
import { simulatorRecommend, formatEUR } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

export default function Simulator() {
  const { t, lang } = useLang();
  const [distance, setDistance] = useState([3]);
  const [desired, setDesired] = useState([100]);
  const [budget, setBudget] = useState([250]);
  const [ambient, setAmbient] = useState("dim");
  const [useCase, setUseCase] = useState("home_cinema");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const id = setTimeout(() => {
      simulatorRecommend({ distance_m: distance[0], ambient, use_case: useCase, budget_eur: budget[0], desired_image_inch: desired[0], lang })
        .then((d) => { setRes(d); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(id);
  }, [distance, desired, budget, ambient, useCase, lang]);

  const ref = res?.reference;

  return (
    <div className="pt-24 pb-16 container-lumeo">
      <div className="text-center max-w-2xl mx-auto mb-8">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl hairline shadow-glow-cyan mx-auto"><Ruler className="h-7 w-7 text-cyanx" /></span>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl">{t("simulator.title")}</h1>
        <p className="mt-3 text-muted-foreground">{t("simulator.subtitle")}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* visualization */}
        <div className="rounded-2xl card-ceramic p-5">
          <RoomVisual distanceM={distance[0]} screenInch={desired[0]} refDiagInch={ref?.image_diagonal_inch} />
          <div className="grid grid-cols-3 gap-3 mt-4">
            <Stat label={t("simulator.diagonal")} value={`${ref?.image_diagonal_inch || "\u2013"}\"`} />
            <Stat label={t("simulator.imageSize")} value={`${(ref?.image_width_m || 0).toFixed(1)}m`} />
            <Stat label={t("simulator.suggestedScreen")} value={`${ref?.suggested_screen_inch || "\u2013"}\"`} />
          </div>
        </div>

        {/* controls */}
        <div className="rounded-2xl card-ceramic p-6 space-y-6">
          <Control label={`${t("simulator.distance")}: ${distance[0].toFixed(1)}m`}>
            <Slider data-testid="simulator-distance-slider" min={1} max={6} step={0.1} value={distance} onValueChange={setDistance} />
          </Control>
          <Control label={`${t("simulator.desiredImage")}: ${desired[0]}\"`}>
            <Slider min={40} max={200} step={5} value={desired} onValueChange={setDesired} />
          </Control>
          <Control label={`${t("simulator.budget")}: ${formatEUR(budget[0])}`}>
            <Slider min={50} max={300} step={10} value={budget} onValueChange={setBudget} />
          </Control>
          <div className="grid grid-cols-2 gap-4">
            <Control label={t("simulator.ambient")}>
              <Select value={ambient} onValueChange={setAmbient}>
                <SelectTrigger data-testid="simulator-ambient-select" className="bg-[#0b0d12] border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border">
                  <SelectItem value="dark">{t("simulator.dark")}</SelectItem>
                  <SelectItem value="dim">{t("simulator.dim")}</SelectItem>
                  <SelectItem value="bright">{t("simulator.bright")}</SelectItem>
                </SelectContent>
              </Select>
            </Control>
            <Control label={t("simulator.useCase")}>
              <Select value={useCase} onValueChange={setUseCase}>
                <SelectTrigger className="bg-[#0b0d12] border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="glass border-border">
                  {["home_cinema", "gaming", "business", "education", "portable", "outdoor"].map((u) => <SelectItem key={u} value={u}>{t(`useCases.${u}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </Control>
          </div>
        </div>
      </div>

      {/* recommendations */}
      <div className="mt-10">
        <h2 className="font-display text-2xl mb-4 flex items-center gap-2">{t("simulator.recommended")} {loading && <Loader2 className="h-4 w-4 animate-spin text-cyanx" />}</h2>
        <div data-testid="simulator-recommendations" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {res?.recommendations?.map((r) => (
            <Link key={r.slug} to={`/product/${r.slug}`} className="rounded-2xl card-ceramic p-4 flex gap-3 hover:shadow-glow-cyan transition-shadow">
              <div className="h-20 w-20 shrink-0 rounded-lg bg-black/40 overflow-hidden"><img src={r.image} alt={r.name} className="h-full w-full object-contain p-1.5" /></div>
              <div className="min-w-0">
                <h3 className="font-display leading-tight">{r.name}</h3>
                <p className="font-mono text-sm mt-0.5">{formatEUR(r.price_eur)}</p>
                <div className="flex flex-wrap gap-1 mt-1.5 text-[11px]">
                  <span className={`inline-flex items-center gap-1 ${r.fits_distance ? "text-mintx" : "text-amberx"}`}>{r.fits_distance ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}{r.fits_distance ? t("simulator.fits") : t("simulator.tooFar")}</span>
                  <span className="text-muted-foreground">· {r.image_diagonal_inch}"</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

const Control = ({ label, children }) => (<div><label className="text-sm text-foreground/80 mb-3 block">{label}</label>{children}</div>);
const Stat = ({ label, value }) => (<div className="rounded-xl hairline p-3 text-center"><p className="font-display text-xl text-cyanx">{value}</p><p className="text-[11px] text-muted-foreground mt-0.5">{label}</p></div>);
